
import { Component, Emit, Prop, PropSync, Vue, Watch } from 'vue-property-decorator';

import Popup from '@/components/utils/Popup.vue';
import AddRoomPopup from '@/components/popup/common/AddRoomPopup.vue';
import RemoveRoomOrSpacePopup from '@/components/popup/common/RemoveRoomOrSpacePopup.vue';
import EditRoomPopup from '@/components/popup/common/EditRoomPopup.vue';
import LogPopup from '@/components/popup/common/LogPopup.vue';
import StatusIcon from '@/components/room/users/StatusIcon.vue';
import {
  GuestPermission,
  Room,
  RoomCount,
  RoomSpace,
  SubRoom,
  TrashBoxSpaceId,
  User,
  UserProperties,
  UserRoomState,
  UserRoomStateData,
} from '@/utils/models';
import { DataService } from '@/service/DataService';
import { isAdmin, uploadFile } from '@/utils/functions';
import MediaIcon from '@/components/utils/MediaIcon.vue';

@Component({
  components: {
    EditRoomPopup,
    RemoveRoomOrSpacePopup,
    AddRoomPopup,
    Popup,
    LogPopup,
    StatusIcon,
    UserRoomStateData,
    MediaIcon,
  },
})
export default class Rooms extends Vue {
  @Prop() selectedSpace!: RoomSpace | null;
  @Prop() selectedRoom!: Room | null;
  @Prop() dataServiceRef!: DataService;
  @Prop() user!: User;
  @Prop() myFavoriteRooms!: Set<string>;
  @Prop() roomCount!: RoomCount;
  @Prop() searchType!: string;
  @Prop() spacesState!: RoomSpace[];
  @Prop() shouldShowSiteLink!: string;
  @Prop() anotherSiteName!: string;

  @Emit()
  requestRoomLogs(request: { roomId: string; range: Date[] }) {}
  @Emit()
  join(room: Room) {}
  @Emit()
  openGlobalSettings() {}
  @Emit()
  goAnotherSite() {}

  isAddModalVisible = false;
  roomToDeleteOrEdit: Room | null = null;
  isRemoveRoomPopupVisible = false;
  isEditRoomPopupVisible = false;
  isLogPopupVisible = false;
  entranceTargetTop = 0;
  entranceTargetLeft = 0;
  adminTarget: Room | null = null;

  searchWordForRoom: string = '';
  descriptionTarget: Room | null = null;
  isAudioEnabledOnInit = false;
  isVideoEnabledOnInit = false;
  get spaces(): RoomSpace[] {
    return this.spacesState;
  }

  get rooms(): Room[] {
    let rooms: Room[] = [];
    if (this.searchType === 'favorites') {
      rooms = this.favoriteRooms;
    } else if (this.searchType === 'all') {
      rooms = this.allRooms.filter((r) => r.spaceId !== TrashBoxSpaceId);
    } else if (this.searchType === 'bySpace') {
      rooms = this.roomsBySpace;
    }
    if (!this.searchWordForRoom) {
      return rooms;
    }
    const upperSearchWord = this.searchWordForRoom.toUpperCase();
    return rooms.filter((r) => {
      return r.name.toUpperCase().includes(upperSearchWord) || r.description?.toUpperCase().includes(upperSearchWord);
    });
  }
  get favoriteRooms(): Room[] {
    return (
      this.spacesState
        .filter((s) => s.spaceType !== 'trash')
        .flatMap((p) => p.rooms.filter((e) => this.myFavoriteRooms.has(e.id))) ?? []
    );
  }
  get allRooms(): Room[] {
    return this.spacesState.flatMap((p) => p.rooms) ?? [];
  }
  get roomsBySpace(): Room[] {
    return this.selectedSpace?.rooms ?? [];
  }

  get isRoomCreatable() {
    return (
      (this.isAdmin || (this.isTrialZawaroomAccount && this.selectedSpace?.spaceType === 'trial-specific-use')) &&
      this.searchType === 'bySpace' &&
      this.selectedSpace &&
      this.selectedSpace.id !== 'trashbox'
    );
  }
  mounted() {
    this.isAudioEnabledOnInit = this.$store.state.isAudioEnabledOnInit;
    this.isVideoEnabledOnInit = this.$store.state.isVideoEnabledOnInit;
  }

  @Watch('searchType')
  @Watch('selectedSpace')
  onSelectedChange() {
    this.searchWordForRoom = '';
  }
  get isAdmin(): boolean {
    return isAdmin(this.user);
  }
  get userAvatar(): string {
    return this.user?.avatarBase64 || '';
  }
  get userAvatarShape(): 'circle' | 'rectangle' {
    return this.user?.avatarShape || 'circle';
  }
  get isTrialZawaroomAccount(): boolean {
    return this.$store.state.siteInfo.isDemo && this.$store.state.user.authType !== 'guest';
  }
  onRequestRoomLogs(request: { roomId: string; range: Date[] }) {
    this.hideLogPopup();
    this.requestRoomLogs(request);
  }
  isRoomAdmin(room: Room): boolean {
    return !!room.adminEmails?.find((e) => e === this.user.email);
  }
  showRemovePopup(room: Room): void {
    if (!room) {
      return;
    }
    this.roomToDeleteOrEdit = room;
    this.isRemoveRoomPopupVisible = true;
  }
  showEditPopup(room: Room): void {
    if (!room) {
      return;
    }
    this.roomToDeleteOrEdit = room;
    this.isEditRoomPopupVisible = true;
  }
  showLogPopup(room: Room): void {
    if (!room) {
      return;
    }
    this.roomToDeleteOrEdit = room;
    this.isLogPopupVisible = true;
  }
  hideRemoveRoomPopup(): void {
    this.isRemoveRoomPopupVisible = false;
  }
  hideEditRoomPopup(): void {
    this.isEditRoomPopupVisible = false;
  }
  hideLogPopup(): void {
    this.isLogPopupVisible = false;
  }

  removeRoom(room: Room): void {
    console.log('remove room', room);
    this.hideRemoveRoomPopup();
    if (room && room.spaceId) {
      this.dataServiceRef.emitRemoveRoom(room.spaceId, room.id);
    }
  }

  async editRoom(room: Room, imageFile: File | null) {
    this.hideEditRoomPopup();
    if (this.selectedSpace && room) {
      const imageUrl = imageFile ? await uploadFile(this.dataServiceRef, 'map-image', imageFile) : room.roomMapUrl;
      let allowDomains = room.allowDomains ?? [];
      let siteAllowDomainNames: string[] = this.$store.state.siteInfo?.allowDomainNames ?? [];
      let newAllowDomains = allowDomains.filter((v) => siteAllowDomainNames.includes(v));
      this.dataServiceRef.emitEditRoom(
        this.selectedSpace.id,
        room.spaceId ?? this.selectedSpace.id,
        room.id,
        room.name,
        room.description ?? '',
        'deny',
        room.roomSize,
        room.subRooms,
        newAllowDomains,
        room.allowUserEmails ?? [],
        room.adminEmails ?? [],
        room.initialScaleMode,
        room.initialScale,
        room.minScaleMode,
        room.minScale,
        room.isPublicRoom ?? false,
        room.defaultVoiceArea,
        room.enteringSoundVolume,
        room.doorSoundVolume,
        imageUrl,
        room.screenShareScope,
        room.enableScreenShareIdling,
        this.$toast.error,
      );
    }
  }

  showAddModal(): void {
    this.isAddModalVisible = true;
  }

  hideAddModal(): void {
    this.isAddModalVisible = false;
  }

  toggleFavorites(room: Room) {
    if (this.myFavoriteRooms.has(room.id)) {
      this.myFavoriteRooms.delete(room.id);
    } else {
      this.myFavoriteRooms.add(room.id);
    }
    const favoriteTargetIds = Array.from(this.myFavoriteRooms);
    this.dataServiceRef.emitUpdateMyFavoriteRooms(favoriteTargetIds);
  }
  showRoomDescriptionBox(room: Room) {
    this.descriptionTarget = room;
  }
  hideRoomDescriptionBox() {
    this.descriptionTarget = null;
  }
  showAdminEmailsBox(room: Room) {
    this.adminTarget = room;
  }
  hideAdminEmailsBox() {
    this.adminTarget = null;
  }
  toggleAudio() {
    this.isAudioEnabledOnInit = !this.isAudioEnabledOnInit;
    this.$store.commit('SetIsAudioEnableOnInit', this.isAudioEnabledOnInit);
  }
  toggleVideo() {
    this.isVideoEnabledOnInit = !this.isVideoEnabledOnInit;
    this.$store.commit('SetIsVideoEnableOnInit', this.isVideoEnabledOnInit);
  }
}
